import { format } from 'date-fns'
import { Activity, Coins, Image, RefreshCw, Type, Video } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import {
  useGetCreditUsageQuery,
  useLazyGetCreditTransactionsQuery,
  type CreditTransaction,
  type CreditUsageCategory,
  type CreditUsageDayBucket,
  type CreditUsageSourceSlice,
} from '../services'

const RANGES = [
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
] as const

// Only Image (single images + carousels, charged as ImageGeneration) and Video
// (reels/shorts) credits are ever charged — TextGeneration is defined but never
// consumed. `other` stays as a catch-all so any future source still reconciles.
const CATEGORY_ORDER: CreditUsageCategory[] = ['image', 'video', 'other']
const ALWAYS_SHOWN: CreditUsageCategory[] = ['image', 'video']

// Acid is the data accent (matches the CreditBar fill on this page); the other
// categories ramp through neutral tones — T3 has no second brand color. Fills use
// arbitrary `var(--token)` values because only `bg-acid`/`bg-paper` are authored
// as standalone utilities.
// `fill` is the Tailwind class used for bars/dots; `color` is the raw CSS value
// for SVG strokes (the pie). Distinct data-viz hue for Video so Image (acid
// green) and Video read apart — deliberate exception to "acid + neutrals".
const CATEGORY_META: Record<
  CreditUsageCategory,
  { label: string; icon: typeof Image; fill: string; color: string }
> = {
  image: { label: 'Image', icon: Image, fill: 'bg-acid', color: 'var(--acid)' },
  video: { label: 'Video', icon: Video, fill: 'bg-[#8b8bff]', color: '#8b8bff' },
  text: {
    label: 'Text',
    icon: Type,
    fill: 'bg-[color:var(--paper-dim)]',
    color: 'var(--paper-dim)',
  },
  other: {
    label: 'Other',
    icon: Coins,
    fill: 'bg-[color:var(--paper-faint)]',
    color: 'var(--paper-faint)',
  },
}

const SOURCE_LABELS: Record<string, string> = {
  ImageGeneration: 'Image generation',
  VideoGeneration: 'Video generation',
  TextGeneration: 'Text generation',
  TopUpPurchase: 'Credits added',
  SubscriptionGrant: 'Subscription grant',
  SubscriptionRenewal: 'Monthly renewal',
  MonthlyExpiry: 'Unused credits expired',
  AdminGrant: 'Admin grant',
  AdminRefund: 'Admin refund',
}

const TYPE_STYLES: Record<string, { label: string; pill: string }> = {
  Consume: { label: 'Used', pill: 'border border-line-bright text-paper-dim' },
  Grant: { label: 'Granted', pill: 'bg-[color:var(--success-tint)] text-[color:var(--success)]' },
  TopUp: { label: 'Added', pill: 'bg-acid-tint text-acid' },
  Refund: { label: 'Refund', pill: 'bg-[color:var(--success-tint)] text-[color:var(--success)]' },
  Expire: { label: 'Expired', pill: 'border border-line text-paper-faint' },
}

/** `YYYY-MM-DD` day key → `Jun 3`. The explicit `T00:00:00` parses as local
 *  midnight, so the label can't drift a day in negative-offset timezones. */
function formatDayLabel(iso: string): string {
  return format(new Date(`${iso}T00:00:00`), 'MMM d')
}

/** Compact relative time (`2h ago`), falling back to `Jun 3` past a week. */
function formatRelative(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return format(new Date(iso), 'MMM d')
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-[12px] border border-line bg-bg-3 p-4">
      <span className="mono text-[10px] tracking-[0.12em] text-paper-faint uppercase">{label}</span>
      <div className="mt-2 text-[26px] font-semibold tracking-[-0.02em] text-paper">{value}</div>
      {hint && <div className="mt-1 text-[12px] text-paper-dim">{hint}</div>}
    </div>
  )
}

function UsageChart({
  byDay,
  categories,
}: {
  byDay: CreditUsageDayBucket[]
  categories: CreditUsageCategory[]
}) {
  const max = Math.max(1, ...byDay.map(d => d.total))
  const labelIdx = new Set(
    [0, Math.floor(byDay.length / 3), Math.floor((2 * byDay.length) / 3), byDay.length - 1].filter(
      i => i >= 0
    )
  )

  const midTick = Math.round(max / 2)

  return (
    <div className="flex gap-2">
      {/* Y axis ticks */}
      <div className="flex h-[180px] w-5 shrink-0 flex-col justify-between text-right text-[10px] leading-none text-paper-faint tabular-nums">
        <span>{max}</span>
        <span>{midTick}</span>
        <span>0</span>
      </div>

      <div className="min-w-0 flex-1">
        <div className="relative flex h-[180px] items-end gap-[3px]">
          {/* Horizontal gridlines aligned to the y-axis ticks */}
          <div className="pointer-events-none absolute inset-0 flex flex-col justify-between">
            <div className="border-t border-line" />
            <div className="border-t border-line" />
            <div className="border-t border-line" />
          </div>
          {byDay.map(day => (
            <div
              key={day.date}
              className="relative flex h-full flex-1 flex-col justify-end gap-[2px]"
              title={`${formatDayLabel(day.date)} · ${day.total} credit${day.total === 1 ? '' : 's'}`}
            >
              {categories.map(cat => {
                const v = day[cat]
                if (!v) return null
                return (
                  <div
                    key={cat}
                    className={`w-full rounded-[2px] ${CATEGORY_META[cat].fill}`}
                    style={{ height: `${(v / max) * 100}%` }}
                  />
                )
              })}
            </div>
          ))}
        </div>
        <div className="mt-2 flex justify-between text-[11px] text-paper-faint">
          {byDay.map((day, i) => (
            <span key={day.date}>{labelIdx.has(i) ? formatDayLabel(day.date) : ''}</span>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Donut chart of credits by category, with a value legend beside it. */
function CreditsPie({ slices }: { slices: CreditUsageSourceSlice[] }) {
  const total = slices.reduce((sum, s) => sum + s.credits, 0) || 1
  const r = 40
  const circumference = 2 * Math.PI * r
  let acc = 0

  return (
    <div className="flex items-center gap-5">
      <svg viewBox="0 0 100 100" className="h-[116px] w-[116px] shrink-0 -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="var(--bg-4)" strokeWidth="16" />
        {slices.map(s => {
          const len = (s.credits / total) * circumference
          const seg = (
            <circle
              key={s.source}
              cx="50"
              cy="50"
              r={r}
              fill="none"
              stroke={CATEGORY_META[s.category].color}
              strokeWidth="16"
              strokeDasharray={`${len} ${circumference - len}`}
              strokeDashoffset={-acc}
            />
          )
          acc += len
          return seg
        })}
      </svg>

      <div className="min-w-0 flex-1 space-y-2.5">
        {slices.map(s => {
          const meta = CATEGORY_META[s.category]
          const pct = Math.round((s.credits / total) * 100)
          return (
            <div key={s.source} className="flex items-center gap-2.5 text-[13px]">
              <i
                className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                style={{ background: meta.color }}
              />
              <span className="flex-1 truncate text-paper">{meta.label}</span>
              <span className="text-paper-dim">
                <span className="font-semibold text-paper">{s.credits}</span> · {pct}%
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const ACTIVITY_PAGE = 15

function RecentActivity({
  businessId,
  memberNames,
}: {
  businessId: string
  memberNames: Map<string, string>
}) {
  // Offset-based pagination: each "Show more" fetches the next page and appends,
  // so there's no ceiling — we page through the whole ledger in small requests.
  const [items, setItems] = useState<CreditTransaction[]>([])
  const [total, setTotal] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [fetchPage, { isFetching }] = useLazyGetCreditTransactionsQuery()

  const loadPage = useCallback(
    (offset: number) => {
      fetchPage({ businessId, limit: ACTIVITY_PAGE, offset })
        .unwrap()
        .then(d => {
          setItems(prev => (offset === 0 ? d.transactions : [...prev, ...d.transactions]))
          setTotal(d.total)
          setLoaded(true)
        })
        .catch(err => {
          // Surface to the console bridge — a failed fetch must not be
          // indistinguishable from a genuinely empty ledger.
          console.error('CreditUsage: failed to load credit transactions:', err)
          setLoaded(true)
        })
    },
    [businessId, fetchPage]
  )

  useEffect(() => {
    loadPage(0)
  }, [loadPage])

  if (!loaded) {
    return (
      <div className="flex justify-center py-6">
        <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-acid" />
      </div>
    )
  }
  if (items.length === 0) {
    return <p className="text-[13px] text-paper-faint">No activity yet.</p>
  }

  const who = (tx: CreditTransaction): string => {
    if (!tx.userId) return 'System'
    return memberNames.get(tx.userId) ?? 'Member'
  }

  const hasMore = items.length < total

  return (
    <div>
      <div className="divide-y divide-line">
        {items.map(tx => {
          const style = TYPE_STYLES[tx.type] ?? {
            label: tx.type,
            pill: 'border border-line text-paper-dim',
          }
          const detail = tx.description ?? SOURCE_LABELS[tx.source] ?? tx.source
          const positive = tx.amount > 0
          return (
            <div key={tx.id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
              <span
                className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${style.pill}`}
              >
                {style.label}
              </span>
              <span className="min-w-0 flex-1 truncate text-[13px] text-paper">{detail}</span>
              <span className="hidden shrink-0 text-[12px] text-paper-faint sm:inline">
                {who(tx)}
              </span>
              <span className="shrink-0 text-[12px] text-paper-faint">
                {formatRelative(tx.createdAt)}
              </span>
              <span
                className={`w-10 shrink-0 text-right text-[13px] font-semibold tabular-nums ${positive ? 'text-[color:var(--success)]' : 'text-paper'}`}
              >
                {positive ? '+' : ''}
                {tx.amount}
              </span>
            </div>
          )
        })}
      </div>

      <div className="mt-3 flex items-center justify-between border-t border-line pt-3">
        <span className="text-[12px] text-paper-faint">
          Showing {items.length} of {total}
        </span>
        {hasMore && (
          <button
            onClick={() => loadPage(items.length)}
            disabled={isFetching}
            className="inline-flex items-center gap-2 rounded-[8px] border border-line-bright px-3 py-1.5 text-[12px] font-medium text-paper transition-colors hover:bg-bg-3 disabled:opacity-50"
          >
            {isFetching ? 'Loading…' : 'Show more'}
          </button>
        )}
      </div>
    </div>
  )
}

export function CreditUsage({
  businessId,
  embedded = false,
}: {
  businessId: string
  /** When rendered inside the analytics modal, the dialog supplies the title,
   *  so we drop the in-component heading and keep just the range toggle. */
  embedded?: boolean
}) {
  const [days, setDays] = useState<number>(30)
  const { data, isLoading, isError, refetch } = useGetCreditUsageQuery({ businessId, days })

  const rangeToggle = (
    <div className="flex gap-1 rounded-[10px] border border-line bg-bg p-1">
      {RANGES.map(r => (
        <button
          key={r.days}
          onClick={() => setDays(r.days)}
          className={`rounded-[7px] px-3 py-1 text-[12px] transition-colors ${
            days === r.days ? 'bg-bg-4 text-paper' : 'text-paper-dim hover:text-paper'
          }`}
        >
          {r.label}
        </button>
      ))}
    </div>
  )

  let body
  if (isLoading) {
    body = (
      <div className="flex justify-center py-10">
        <div className="h-7 w-7 animate-spin rounded-full border-b-2 border-acid" />
      </div>
    )
  } else if (isError || !data) {
    body = (
      <div className="space-y-3 py-8 text-center">
        <p className="text-[13px] text-paper-dim">Couldn't load usage.</p>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 rounded-[8px] border border-line-bright bg-bg-2 px-4 py-2 text-[13px] font-medium text-paper transition-colors hover:bg-bg-3"
        >
          <RefreshCw size={14} />
          Retry
        </button>
      </div>
    )
  } else if (data.totals.consumed === 0) {
    body = (
      <div className="rounded-[12px] border border-line bg-bg-2 px-6 py-10 text-center">
        <Activity size={22} className="mx-auto text-paper-faint" />
        <p className="mt-3 text-[14px] font-medium text-paper">No credits used yet</p>
        <p className="mt-1 text-[12px] text-paper-dim">
          Usage shows up here as your team generates content.
        </p>
      </div>
    )
  } else {
    const { totals, byDay, bySource, byMember } = data
    // Legend always shows Image + Video as a colour key, plus any other category
    // that actually has spend — so the colours are visible even on an image-only
    // account. (The chart itself stacks every category in CATEGORY_ORDER.)
    const legendCategories = CATEGORY_ORDER.filter(
      c => ALWAYS_SHOWN.includes(c) || byDay.some(d => d[c] > 0)
    )
    const memberNames = new Map<string, string>(
      byMember.filter(m => m.userId && m.name).map(m => [m.userId as string, m.name as string])
    )

    body = (
      <div className="space-y-4">
        {/* Everything in this box reflects the selected period (range toggle) */}
        <section className="overflow-hidden rounded-[16px] border border-line bg-bg-2">
          <div className="flex items-center justify-end gap-3 px-5 pt-4">{rangeToggle}</div>

          <div className="space-y-4 p-5">
            {/* KPIs */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <StatCard
                label="Used"
                value={String(totals.consumed)}
                hint={`last ${data.rangeDays} days`}
              />
              <StatCard label="Added" value={`+${totals.granted}`} hint="grants + purchases" />
            </div>

            {/* Usage over time */}
            <div className="rounded-[12px] border border-line bg-bg-3 p-5">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h4 className="text-[14px] font-semibold text-paper">Usage over time</h4>
                <div className="flex flex-wrap justify-end gap-3">
                  {legendCategories.map(cat => (
                    <span
                      key={cat}
                      className="flex items-center gap-1.5 text-[12px] text-paper-dim"
                    >
                      <i className={`h-2.5 w-2.5 rounded-[3px] ${CATEGORY_META[cat].fill}`} />
                      {CATEGORY_META[cat].label}
                    </span>
                  ))}
                </div>
              </div>
              <UsageChart byDay={byDay} categories={CATEGORY_ORDER} />
            </div>

            {/* Where credits went + by member */}
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <div className="rounded-[12px] border border-line bg-bg-3 p-5">
                <h4 className="mb-4 text-[14px] font-semibold text-paper">Where credits went</h4>
                {bySource.length === 0 ? (
                  <p className="text-[13px] text-paper-faint">No generation spend in this range.</p>
                ) : (
                  <CreditsPie slices={bySource} />
                )}
              </div>

              <div className="rounded-[12px] border border-line bg-bg-3 p-5">
                <h4 className="mb-4 text-[14px] font-semibold text-paper">By team member</h4>
                {byMember.length === 0 ? (
                  <p className="text-[13px] text-paper-faint">No attributed spend in this range.</p>
                ) : (
                  <div className="divide-y divide-line">
                    {byMember.map(member => {
                      const name = member.name ?? 'Unknown'
                      const initials = name
                        .split(' ')
                        .map(p => p[0])
                        .slice(0, 2)
                        .join('')
                        .toUpperCase()
                      return (
                        <div
                          key={member.userId ?? 'unknown'}
                          className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
                        >
                          <div className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-bg-4 text-[11px] font-semibold text-paper-dim">
                            {initials || '–'}
                          </div>
                          <span className="flex-1 text-[13.5px] text-paper">{name}</span>
                          <span className="text-[13px] text-paper-dim">
                            <span className="font-semibold text-paper">{member.credits}</span>{' '}
                            credits
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Recent activity — a separate box; NOT tied to the range toggle */}
        <section className="card p-5">
          <h4 className="mb-4 text-[14px] font-semibold text-paper">Recent activity</h4>
          <RecentActivity businessId={businessId} memberNames={memberNames} />
        </section>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {!embedded && (
        <h3 className="text-[16px] font-semibold tracking-[-0.005em] text-paper">Usage</h3>
      )}
      {body}
    </div>
  )
}
